import { useCallback } from 'react';
import { computeErFocus, type TourStep } from '../../lib/tour';
import { useSchemaStore } from '../../store/useSchemaStore';
import TourPlayer from '../tour/TourPlayer';

/**
 * The guided story of a database diagram: each step lights up the tables it links to (a group lights its whole box),
 * marks the fields it names, and highlights the relationships between the lit tables.
 */
export default function ErTourOverlay() {
  const documentation = useSchemaStore((s) => s.documentation);
  const schema = useSchemaStore((s) => s.schema);

  const focusFor = useCallback((step: TourStep) => computeErFocus(step, schema), [schema]);
  const resolveReference = useCallback(
    (kind: string, target: string) => {
      const same = (a: string, b: string) => a.toLowerCase() === b.toLowerCase();
      if (kind === 'table' || kind === 'column') {
        const table = schema.tables.find((t) => same(t.name, kind === 'column' ? target.split('.')[0] : target));
        return table ? [table.id] : null;
      }
      if (kind === 'group') {
        const group = schema.groups.find((g) => same(g.name, target));
        return group ? [`group:${group.id}`] : null;
      }
      return null;
    },
    [schema],
  );

  return (
    <TourPlayer
      documentation={documentation}
      focusFor={focusFor}
      focusKey={focusFor}
      resolveReference={resolveReference}
      emptyHint={
        <>
          Add numbered steps to its explanation, as headings that begin <code>### Step 1:</code>, with table links such as <code>[booking](#table:booking)</code> in each step.
        </>
      }
    />
  );
}
